import classNames from "classnames";
import LoadingDots from "@components/_Layout/Loaders/LoadingDots";
import PropTypes from "prop-types";
import { ColorVariantType } from "../../../../types";
import { useConfig } from "@hooks";

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ColorVariantType[`variant`] | `light` | `gradient` | `black`;
  shape?: `default` | `pills` | `outline` | "outline-pills" | `link`;
  size?: `sm` | `md` | `lg`;
  isLoading?: boolean;
  loadingChildren?: JSX.Element;
  additionalClassname?: string;
}

const Button: React.FC<ButtonProps> = ({
  shape = `default`,
  variant = `primary`,
  size = `md`,
  isLoading,
  loadingChildren,
  additionalClassname,
  children,
  ...props
}) => {
  const { mode: clientMode } = useConfig();
  const mode = clientMode ? clientMode : "vacanceole";

  const BUTTON_CLASS = {
    base: `tw-py-1 tw-px-3 tw-cursor-pointer tw-outline-none focus:tw-outline-none tw-transition-colors tw-duration-200 tw-ease-in-out tw-font-montserrat`,
    disabled: `disabled:tw-opacity-50 tw-cursor-auto`,
    variant: {
      gradient: {
        shape: {
          default: `tw-uppercase tw-text-white tw-w-full md:tw-w-[300px] tw-bg-v_blue-dark tw-rounded-full`,
          pills: `tw-uppercase tw-text-white tw-bg-v_blue-dark tw-rounded-full`,
          outline: `tw-uppercase tw-border tw-border-gray-300 tw-bg-white tw-text-gray-800 tw-bg-v_blue-dark hover:tw-border-white hover:tw-text-gray-800 tw-rounded-full`,
          "outline-pills": `tw-uppercase tw-border tw-border-gray-300 tw-bg-white tw-text-gray-800 hover:tw-border-white hover:tw-text-gray-800 tw-rounded-full`,
          link: `tw-uppercase tw-text-gray-500 hover:tw-text-gray-600 tw-font-dosis tw-font-semibold tw-underline tw-underline-offset-[6px]`,
        },
      },
      default: {
        shape: {
          default: `tw-border tw-border-gray-200 tw-text-gray-800 tw-bg-gray-200 hover:tw-bg-gray-300 tw-rounded`,
          pills: `tw-border tw-border-gray-300 tw-text-gray-800 tw-bg-gray-200 hover:tw-bg-gray-300 tw-rounded-full`,
          outline: `tw-border tw-border-gray-300 tw-bg-white tw-text-gray-800 hover:tw-bg-gray-300 hover:tw-border-white hover:tw-text-gray-800 tw-rounded`,
          "outline-pills": `tw-border tw-border-gray-300 tw-bg-white tw-text-gray-800 hover:tw-bg-gray-300 hover:tw-border-white hover:tw-text-gray-800 tw-rounded-full`,
          link: `tw-text-gray-500 hover:tw-text-gray-600 tw-font-dosis tw-font-semibold tw-underline tw-underline-offset-[6px]`,
        },
      },
      black: {
        shape: {
          default: `tw-border tw-border-v_blue-dark tw-text-white tw-bg-v_blue-dark hover:tw-bg-v_blue-dark/80 tw-rounded`,
          pills: `tw-border tw-border-v_blue-dark tw-text-white tw-bg-v_blue-dark hover:tw-bg-v_blue-dark/80 tw-rounded-full`,
          outline: `tw-border tw-border-v_blue-dark tw-bg-white tw-text-v_blue-dark hover:tw-bg-v_blue-dark hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-v_blue-dark tw-bg-white tw-text-v_blue-dark hover:tw-bg-v_blue-dark hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-black hover:tw-text-black/70 tw-font-dosis tw-font-semibold tw-underline tw-underline-offset-[6px]`,
        },
      },
      primary: {
        shape: {
          default: `tw-border tw-w-full md:tw-w-[300px] ${
            mode === "vacanceole"
              ? "tw-text-v_blue-dark tw-border-v_rose tw-bg-v_rose hover:tw-bg-v_rose/80"
              : "tw-text-white tw-border-primary_corseole-500 tw-bg-primary_corseole-500 hover:tw-bg-primary_corseole-400"
          } tw-rounded`,
          pills: `tw-border ${
            mode === "vacanceole"
              ? "tw-text-v_blue-dark tw-border-v_rose tw-bg-v_rose hover:tw-bg-v_rose/80"
              : "tw-text-white tw-border-primary_corseole-500 tw-bg-primary_corseole-500 hover:tw-bg-primary_corseole-400"
          } tw-rounded-full`,
          outline: `tw-border tw-bg-white ${
            mode === "vacanceole"
              ? "tw-border-v_blue-dark tw-text-v_blue-dark hover:tw-bg-v_blue-dark"
              : "tw-border-primary_corseole-500 tw-text-primary_corseole-500 hover:tw-bg-primary_corseole-500"
          } hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-v_blue-dark tw-bg-white tw-text-v_blue-dark hover:tw-bg-v_blue-dark hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `${
            mode === "vacanceole"
              ? "tw-text-v_blue-dark hover:tw-text-v_blue-dark/80"
              : "tw-text-primary_corseole-500 hover:tw-text-primary_corseole-400"
          } tw-font-dosis tw-font-semibold tw-underline tw-underline-offset-[6px]`,
        },
      },
      light: {
        shape: {
          default: `tw-border tw-text-white tw-w-full md:tw-w-[300px] ${
            mode === "vacanceole"
              ? "tw-border-v_blue-light tw-bg-v_blue-light hover:tw-bg-v_blue-light/80"
              : "tw-border-primary_corseole-500 tw-bg-primary_corseole-500 hover:tw-bg-primary_corseole-400"
          } tw-rounded`,
          pills: `tw-border tw-text-v_blue-dark ${
            mode === "vacanceole"
              ? "tw-border-v_blue-light tw-bg-v_blue-light hover:tw-bg-v_blue-light/80"
              : "tw-border-primary_corseole-500 tw-bg-primary_corseole-500 hover:tw-bg-primary_corseole-400"
          } tw-rounded-full`,
          outline: `tw-border tw-bg-white ${
            mode === "vacanceole"
              ? "tw-border-v_blue-light tw-text-v_blue-light hover:tw-bg-v_blue-light"
              : "tw-border-primary_corseole-500 tw-text-primary_corseole-500 hover:tw-bg-primary_corseole-500"
          } hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-v_blue-light tw-bg-white tw-text-v_blue-light hover:tw-bg-v_blue-light hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `${
            mode === "vacanceole"
              ? "tw-text-v_blue-light hover:tw-text-v_blue-light/80"
              : "tw-text-primary_corseole-500 hover:tw-text-primary_corseole-400"
          } tw-font-dosis tw-font-semibold tw-underline tw-underline-offset-[6px]`,
        },
      },
      secondary: {
        shape: {
          default: `tw-border tw-border-secondary-500 tw-text-white tw-bg-secondary-500 hover:tw-bg-secondary-400 tw-rounded`,
          pills: `tw-border tw-border-secondary-500 tw-text-white tw-bg-secondary-500 hover:tw-bg-secondary-400 tw-rounded-full`,
          outline: `tw-border tw-border-secondary-500 tw-bg-white tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-secondary-500 tw-bg-white tw-text-secondary-500 hover:tw-bg-secondary-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-secondary-500 hover:tw-text-secondary-400 tw-font-dosis tw-font-semibold tw-underline tw-underline-offset-[6px]`,
        },
      },
      success: {
        shape: {
          default: `tw-border tw-border-green-500 tw-text-white tw-bg-green-500 hover:tw-bg-green-400 tw-rounded`,
          pills: `tw-border tw-border-green-500 tw-text-white tw-bg-green-500 hover:tw-bg-green-400 tw-rounded-full`,
          outline: `tw-border tw-border-green-500 tw-bg-white tw-text-green-500 hover:tw-bg-green-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-green-500 tw-bg-white tw-text-green-500 hover:tw-bg-green-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-green-500 hover:tw-text-green-400`,
        },
      },
      info: {
        shape: {
          default: `tw-border tw-border-blue-500 tw-text-white tw-bg-blue-500 hover:tw-bg-blue-400 tw-rounded`,
          pills: `tw-border tw-border-blue-500 tw-text-white tw-bg-blue-500 hover:tw-bg-blue-400 tw-rounded-full`,
          outline: `tw-border tw-border-blue-500 tw-bg-white tw-text-blue-500 hover:tw-bg-blue-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-blue-500 tw-bg-white tw-text-blue-500 hover:tw-bg-blue-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-blue-500 hover:tw-text-blue-400`,
        },
      },
      warning: {
        shape: {
          default: `tw-border tw-border-yellow-500 tw-text-white tw-bg-yellow-500 hover:tw-bg-yellow-400 tw-rounded`,
          pills: `tw-border tw-border-yellow-500 tw-text-white tw-bg-yellow-500 hover:tw-bg-yellow-400 tw-rounded-full`,
          outline: `tw-border tw-border-yellow-500 tw-bg-white tw-text-yellow-500 hover:tw-bg-yellow-500 hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-yellow-500 tw-bg-white tw-text-yellow-500 hover:tw-bg-yellow-500 hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-yellow-500 hover:tw-text-yellow-400`,
        },
      },
      danger: {
        shape: {
          default: `tw-border tw-border-v_red-dark tw-text-white tw-bg-v_red-dark hover:tw-bg-v_red-dark/80 tw-rounded`,
          pills: `tw-border tw-border-v_red-dark tw-text-white tw-bg-v_red-dark hover:tw-bg-v_red-dark/80 tw-rounded-full`,
          outline: `tw-border tw-border-v_red-dark tw-bg-white tw-text-v_red-dark hover:tw-bg-v_red-dark hover:tw-border-white hover:tw-text-white tw-rounded`,
          "outline-pills": `tw-border tw-border-v_red-dark tw-bg-white tw-text-v_red-dark hover:tw-bg-v_red-dark hover:tw-border-white hover:tw-text-white tw-rounded-full`,
          link: `tw-text-v_red-dark hover:tw-text-v_red-dark/80`,
        },
      },
    },
    size: {
      sm: `tw-text-sm tw-leading-6`,
      md: `tw-text-base tw-leading-8`,
      lg: `tw-text-lg tw-leading-10`,
    },
  };

  let _buttonClass = classNames(
    BUTTON_CLASS.base,
    BUTTON_CLASS.variant[variant].shape[shape],
    (props.disabled || isLoading) && BUTTON_CLASS.disabled,
    BUTTON_CLASS.size[size],
    additionalClassname
  );
  if (props.className) _buttonClass = props.className;

  return (
    <button
      disabled={props.disabled || isLoading}
      className={_buttonClass}
      {...props}
    >
      {isLoading ? (
        loadingChildren ? (
          loadingChildren
        ) : (
          <span>
            <LoadingDots />
          </span>
        )
      ) : (
        <>{children}</>
      )}
    </button>
  );
};

Button.propTypes = {};

export default Button;
